<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';
    import BaseSelect from '@/components/BaseSelectComponent.vue';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useUserStore } from '@/stores/user/userStore';

    // Vue
    import { computed, onMounted, reactive, inject, watch, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // 定数
    // ----------------------------
    const router = useRouter();
    const commonStore = useCommonStore();
    const reserveStore = useReserveStore();
    const languageStore = useLanguageStore();
    const userStore = useUserStore();

    const app = getCurrentInstance();
    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    // 対象項目の確認用フラグ
    let findFlag = false;

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const requestData = reactive(reserveStore.rsvdtlRequest);
    // ご相談内容1のリスト
    const firstIssue = ref([]);

    // ご相談内容2のリスト
    const secondIssue = ref([]);

    // ----------------------------
    // 関数
    // ----------------------------
    // レスポンスデータをリクエストにマージ
    function mergeResponce(target, res) {
        for (const key in res) {
            if (key in target && !Utils.isNullEmpty(res[key])) {
                target[key] = res[key];
            }
        }
        return target;
    }
    /**
     * 前回と同じ内容を選択ボタン押下時
     */
    async function onSameContent() {
        // 予約取得API
        const getReserveRequest = {
            Lang: languageStore.getLanguageValue,
            // ユーザー情報から取得
            ReservationPk: userStore.userModel.PrevReservePk,
        };
        const response = await reserveStore.getDetailAction(API_URL, getReserveRequest);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        } else {
            //予約取得APIで取得した値を格納
            reserveStore.reserveDetail.ReservationDt = '';
            mergeResponce(requestData, reserveStore.reserveDetail)
            // セッショングループ情報は次回予約へ引き継がない
            requestData.SessionGrpPk = '';
            reserveStore.restoreFlag = true;

            // イシュー一覧取得API
            const issuesRequestBody = {
                Lang: languageStore.getLanguageValue,
                SelfUseMethod: '1',
                ServicePk: reserveStore.reserveDetail.ServicePk,
            };
            const response = await reserveStore.getIssuesAction(API_URL, issuesRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            } else {
                // 取得したIssuePk1がイシュー一覧に存在しない場合、ご相談内容1を「(選択してください)」へ初期化処理
                for (let i = 0; i < reserveStore.issueList.length; i++) {
                    findFlag = false;
                    if (reserveStore.issueList[i].pk === requestData.IssuePk1) {
                        findFlag = true;
                        break;
                    }
                }
                if (!findFlag) {
                    requestData.IssuePk1 = '';
                }
                // 取得したIssuePk2がイシュー一覧に存在しない場合、ご相談内容2を「(選択してください)」へ初期化処理
                for (let i = 0; i < reserveStore.issueList.length; i++) {
                    findFlag = false;
                    if (reserveStore.issueList[i].pk === requestData.IssuePk2) {
                        findFlag = true;
                        break;
                    }
                }
                if (!findFlag) {
                    requestData.IssuePk2 = '';
                }

                //ご相談内容1が選択してくださいでない場合
                if (requestData.IssuePk1 !== '') {
                    // 予約利用方法一覧API
                    let issue1SelectedManagementRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        SelfUseMethod: '1',
                        IssuePk1: requestData.IssuePk1,
                    };
                    //IssuePk2が空でない場合、リクエストボディにIssuePk2を追加する処理
                    if (requestData.IssuePk2 !== '') {
                        issue1SelectedManagementRequestBody['IssuePk2'] = requestData.IssuePk2;
                    }
                    const response = await reserveStore.getManegmentAction(API_URL, issue1SelectedManagementRequestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    } else {
                        // 取得した利用方法が予約利用方法一覧に存在しない場合、ご利用方法を初期化処理
                        for (let i = 0; i < reserveStore.cngMethodTypes.length; i++) {
                            findFlag = false;
                            if (reserveStore.cngMethodTypes[i] === requestData.CngMethodType) {
                                findFlag = true;
                                break;
                            }
                        }
                        if (!findFlag) {
                            requestData.CngMethodType = '';
                        }

                        // 利用方法が空でない場合
                        if (requestData.CngMethodType !== '') {
                            // 予約利用言語一覧API
                            const languageRequestBody = {
                                Lang: languageStore.getLanguageValue,
                                SelfUseMethod: '1',
                                CngMethodType: requestData.CngMethodType,
                                IssuePk1: requestData.IssuePk1,
                            };
                            //IssuePk2が空でない場合、リクエストボディにIssuePk2を追加する処理
                            if (requestData.IssuePk2 !== '') {
                                languageRequestBody['IssuePk2'] = requestData.IssuePk2;
                            }
                            const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                            if (!response.success) {
                                messageDialog('error', response.errorMessage);
                                if (response.redirect) {
                                    location.href = response.redirectUrl;
                                }
                                return false;
                            } else {
                                // 取得した言語pkが予約利用言語一覧に存在しない場合、ご利用言語を初期化処理
                                for (let i = 0; i < reserveStore.languageList.length; i++) {
                                    findFlag = false;
                                    if (reserveStore.languageList[i].pk === requestData.CngLanguagePk) {
                                        findFlag = true;
                                        break;
                                    }
                                }
                                if (!findFlag) {
                                    requestData.CngLanguagePk = '';
                                }
                            }
                        }
                    }
                    // ご相談内容1が選択してくださいでない場合
                } else {
                    // ご相談内容2の選択項目の初期化
                    requestData.IssuePk2 = '';
                    // ご利用方法の選択項目の初期化
                    reserveStore.cngMethodTypes = '';
                    requestData.CngMethodType = '';
                    // ご利用言語の選択項目の初期化
                    reserveStore.languageList = '';
                    requestData.CngLanguagePk = '';
                    // 復元状況確認フラグの初期化
                    reserveStore.restoreFlag = false;
                    // 取得済みの復元内容を破棄し、イシュー一覧を絞り込み前の状態へ戻す
                    reserveStore.clearReserveDetail();
                    reserveStore.issueList = firstIssue.value;
                }
            }
        }
    }

    /**
     * 戻るボタン押下時
     */
    function onBack() {
        // 遷移元画面をリセットする。
        commonStore.setFrom('');

        // リクエスト情報クリア
        reserveStore.clearAll();
        //reserveStore.clearRequestData();

        // 遷移元の画面に戻る。遷移元はホーム画面。
        //router.back();
        router.push({ name: 'home' });
    }

    /**
     * 進むボタン押下時
     */
    // サービスが一件かつ規定PKが存在する場合、予約(規定同意)画面に遷移
    async function onAdvance() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // 遷移元画面を「予約(相談内容から利用方法等選択)」に設定する。
        commonStore.setFrom('consultation');

        // 予約サービス一覧取得APIのリクエストボディを生成する
        let requestBody = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '1',
            IssuePk1: requestData.IssuePk1,
            CngMethodType: requestData.CngMethodType,
            CngLanguagePk: requestData.CngLanguagePk,
        };

        //IssuePk2が空でない場合、リクエストボディにIssuePk2を追加する処理
        if (!Utils.isNullEmpty(requestData.IssuePk2)) {
            requestBody['IssuePk2'] = requestData.IssuePk2;
        }

        // 予約サービス一覧取得APIをコールする。
        const response = await reserveStore.getReserveServiceAction(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }

        // 復元状態を確認する。
        if (!reserveStore.restoreFlag) {
            // 復元状況確認フラグがfalseの場合。
            // 「前回と同じ内容を選択」ボタンをクリック後に、何かしらの編子うを加えた場合や、通常の登録ルートは本ルートに入る。
            if (reserveStore.serviceList.length >= 2 || reserveStore.serviceList.length === 0) {
                // 構造を記録する。
                reserveStore.setRsvdtlRequestData(requestData);

                // サービス一覧が複数件、または0件の場合
                // 予約(相談内容からサービス選択)画面へ遷移する。
                router.push('/reserve/planning/service');
            } else {
                // サービスが1件しかない場合
                requestData.ServicePk = reserveStore.serviceList[0].ServicePk;

                // サービス詳細取得APIのクエリパラメーターを生成する。
                const queryParam2 = {
                    Lang: languageStore.getLanguageValue,
                    ServicePk: reserveStore.serviceList[0].ServicePk,
                };

                // サービス詳細取得APIをコールする。
                const response2 = await reserveStore.getServiceDetailAction(API_URL, queryParam2);

                if (!response2.success) {
                    messageDialog('error', response2.errorMessage);
                    if (response2.redirect) {
                        location.href = response2.redirectUrl;
                    }
                    return false;
                } else {
                    // サービス詳細取得APIから規約PK情報を抽出する。
                    requestData.RegulationPk = reserveStore.serviceDetail.RegulationPk;

                    // 構造を記録する。
                    reserveStore.setRsvdtlRequestData(requestData);

                    // 規約PKの有無を判定する。
                    if (!Utils.isNullEmpty(requestData.RegulationPk)) {
                        // 規約PKが存在する場合は、必ず規約同意画面へ遷移する。
                        // 予約(規定同意)画面に遷移する。
                        router.push({ name: 'reserveplanningagree' });
                    } else {
                        // 規定PKが存在しない場合
                        // 予約(場所日時選択-カレンダー)画面および予約(場所日時選択-アレンジ)画面へ遷移する。
                        router.push('/reserve/planning/counselor');
                        
                    }
                }
            }
        } else {
            // 復元状況確認フラグがtrueの場合。
            // 「前回と同じ内容を選択」ボタンをクリック後、何も変更していない場合のルート。
            requestData.ServicePk = reserveStore.reserveDetail.ServicePk;
            const queryParam = {
                Lang: languageStore.getLanguageValue,
                ServicePk: requestData.ServicePk,
            };
            // サービス詳細取得API
            const response = await reserveStore.getServiceDetailAction(API_URL, queryParam);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            }

            // 予約(場所日時選択-カレンダー)画面および予約(場所日時選択-アレンジ)画面へ遷移する。
            router.push('/reserve/planning/counselor');
        }
    }

    /**
     * ご相談内容1変化時
     */
    async function issueChange1() {
        // 復元状況確認フラグの初期化
        reserveStore.restoreFlag = false;
        // 取得済みの復元内容を破棄し、イシュー一覧を絞り込み前の状態へ戻す
        reserveStore.clearReserveDetail();
        reserveStore.issueList = firstIssue.value;
        // ご相談内容1選択済、ご利用方法・ご利用言語未選択の場合
        if (requestData.IssuePk1 !== '' && requestData.CngMethodType === '' && requestData.CngLanguagePk === '') {
            requestData.IssuePk2 = '';
            const issue1SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                SelfUseMethod: '1',
                IssuePk1: requestData.IssuePk1,
            };
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue1SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            }
            // ご相談内容1・ご利用方法・ご利用言語選択済の場合
        } else if (requestData.IssuePk1 !== '' && requestData.CngMethodType !== '' && requestData.CngLanguagePk !== '') {
            requestData.IssuePk2 = '';
            const issue2SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                SelfUseMethod: '1',
                IssuePk1: requestData.IssuePk1,
            };
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            } else {
                const languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    CngMethodType: requestData.CngMethodType,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                };
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
            // ご相談内容1・ご利用方法選択済、ご利用言語未選択の場合
        } else if (requestData.IssuePk1 !== '' && requestData.CngMethodType !== '' && requestData.CngLanguagePk === '') {
            requestData.IssuePk2 = '';
            const issue2SelectedManagementRequestBody = {
                Lang: languageStore.getLanguageValue,
                SelfUseMethod: '1',
                IssuePk1: requestData.IssuePk1,
            };
            // 予約利用方法一覧API
            const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
            if (!response.success) {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return false;
            } else {
                const languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    CngMethodType: requestData.CngMethodType,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                };
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
        } else {
            reset();
        }
    }

    /**
     * ご相談内容2変化時
     */
    async function issueChange2() {
        // 復元状況確認フラグの初期化
        reserveStore.restoreFlag = false;
        // 取得済みの復元内容を破棄し、イシュー一覧を絞り込み前の状態へ戻す
        reserveStore.clearReserveDetail();
        reserveStore.issueList = firstIssue.value;
        // ご相談内容1が選択済の場合
        if (requestData.IssuePk1 !== '') {
            // ご相談内容2が選択済の場合
            if (requestData.IssuePk2 !== '') {
                const issue2SelectedManagementRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // 予約利用方法一覧API
                await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);

                // ご利用方法が選択済の場合
                if (requestData.CngMethodType !== '') {
                    const languageRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        CngMethodType: requestData.CngMethodType,
                        SelfUseMethod: '1',
                        IssuePk1: requestData.IssuePk1,
                        IssuePk2: requestData.IssuePk2,
                    };
                    // 予約利用言語一覧API
                    const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    }
                }
                // ご相談内容2が未選択の場合
            } else {
                const issue2SelectedManagementRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                };
                // 予約利用方法一覧API
                const response = await reserveStore.getManegmentAction(API_URL, issue2SelectedManagementRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                } else {
                    const languageRequestBody = {
                        Lang: languageStore.getLanguageValue,
                        CngMethodType: requestData.CngMethodType,
                        SelfUseMethod: '1',
                        IssuePk1: requestData.IssuePk1,
                    };
                    // 予約利用言語一覧API
                    const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return false;
                    }
                }
            }
        }
    }

    /**
     * ご利用方法変化時
     */
    async function manegmentChange() {
        // 復元状況確認フラグの初期化
        reserveStore.restoreFlag = false;
        // 取得済みの復元内容を破棄し、イシュー一覧を絞り込み前の状態へ戻す
        reserveStore.clearReserveDetail();
        reserveStore.issueList = firstIssue.value;
        // ご相談内容1が選択済の場合
        if (requestData.IssuePk1 !== '') {
            // ご相談内容2選択済、ご利用言語未取得の場合
            if (requestData.IssuePk2 !== '' && reserveStore.languageList === '') {
                const languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    CngMethodType: requestData.CngMethodType,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
                // ご相談内容2が選択済の場合
            } else if (requestData.IssuePk2 !== '') {
                requestData.CngLanguagePk = '';
                const languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    CngMethodType: requestData.CngMethodType,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                    IssuePk2: requestData.IssuePk2,
                };
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
                // ご相談内容2が未選択の場合
            } else {
                requestData.CngLanguagePk = '';
                const languageRequestBody = {
                    Lang: languageStore.getLanguageValue,
                    CngMethodType: requestData.CngMethodType,
                    SelfUseMethod: '1',
                    IssuePk1: requestData.IssuePk1,
                };
                // 予約利用言語一覧API
                const response = await reserveStore.getLanguageAction(API_URL, languageRequestBody);
                if (!response.success) {
                    messageDialog('error', response.errorMessage);
                    if (response.redirect) {
                        location.href = response.redirectUrl;
                    }
                    return false;
                }
            }
        }
    }

    /**
     * ご相談言語変化時
     */
    async function languageChange() {
        // 復元状況確認フラグの初期化
        reserveStore.restoreFlag = false;
        // 取得済みの復元内容を破棄し、イシュー一覧を絞り込み前の状態へ戻す
        reserveStore.clearReserveDetail();
        reserveStore.issueList = firstIssue.value;
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // ご相談内容1
        if (Utils.isNullEmpty(requestData.IssuePk1)) {
            message += i18n.global.t('reservePlanningConsultation.message.message1') + '\n';
        }
        // ご利用方法
        if (Utils.isNullEmpty(requestData.CngMethodType)) {
            message += i18n.global.t('reservePlanningConsultation.message.message2') + '\n';
        }
        // ご利用言語
        if (Utils.isNullEmpty(requestData.CngLanguagePk)) {
            message += i18n.global.t('reservePlanningConsultation.message.message3') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 変数初期化処理
     */
    function reset() {
        // ご相談内容1の選択状況の初期化
        requestData.IssuePk1 = '';
        // ご相談内容2の選択状況の初期化
        requestData.IssuePk2 = '';
        // ご利用方法の選択項目の初期化
        reserveStore.cngMethodTypes = '';
        requestData.CngMethodType = '';
        // ご利用言語の選択項目の初期化
        reserveStore.languageList = '';
        requestData.CngLanguagePk = '';
    }

    // ----------------------------
    // 算術プロパティ
    // ----------------------------
    // ご相談内容1の内容を消す処理
    const filteredIssues = computed(() => {
        return firstIssue.value.filter((issue) => issue.pk !== requestData.IssuePk1);
    });

    watch(filteredIssues, (secondFilteredIssue) => {
        secondIssue.value = secondFilteredIssue;
    });

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // 前画面から戻ってきたか判定する。
        if (!commonStore.reservePageBack) {
            // 「前回と同じ内容を選択」ボタンがクリックされた場合の復元フラグを初期化する。
            // 復元フラグの初期値はfalseで、「前回と同じ内容を選択」ボタンがクリックされ、
            // 前回内容が取得できた場合にtrueになる。
            // trueになった状態で復元内容の選択を変更した時点で、新しい予約の設定とみなし、フラグをfalseに戻す。
            reserveStore.restoreFlag = false;
            reserveStore.clearReserveDetail();

            // 遷移元画面をリセットする。
            commonStore.setFrom('');

            // 本画面の選択を初期化する。
            reset();
        } else {
            // 前画面から戻ってきた場合、座標記録をクリアする。
            reserveStore.clearReserveStep();
        }

        // 前画面からの戻るフラグを初期化する。
        commonStore.reservePageBack = false;

        // 戻り先座標を記録する。
        // 遷移元予約画面のルートと、設定・選択状況に応じて、戻る画面を制御する。
        // 相談内容を選んで予約のルート
        reserveStore.reserveStep.consultation.reserveplanningconsultation = true;

        // ユーザー情報取得API
        await userStore.getUserAction(API_URL, null);

        // イシュー一覧取得APIのリクエストボディを生成する。
        const issuesRequestBody = {
            Lang: languageStore.getLanguageValue,
            SelfUseMethod: '1',
        };

        // イシュー一覧取得APIをコールする。
        const response = await reserveStore.getIssuesAction(API_URL, issuesRequestBody);
        if (response.success) {
            // APIから取得した結果で、「相談内容1」のプルダウンの選択肢を生成する。
            firstIssue.value = reserveStore.issueList;

            // 相談内容2の選択肢を初期化する。
            secondIssue.value = [];
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
    });
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reservePlanningConsultation.parts.pageTitle1') }}</span>
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode1_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode1_step1_en.png" class="step" width="410" height="46" />
                    </template>
                </h3>
                <section class="whiteBoxContents scroll">
                    <!-- ステップ表示 -->
                    <template v-if="languageStore.getLanguageValue === '1'">
                        <img src="/img/step/mode1_step1.png" class="step" width="410" height="46" />
                    </template>
                    <template v-else>
                        <img src="/img/step/mode1_step1_en.png" class="step" width="410" height="46" />
                    </template>
                    <ul class="btnArea center" style="margin-bottom: 20px">
                        <li>
                            <!--前回と同じ内容を選択-->
                            <button type="button" class="btnSecondary wide" @click="onSameContent" :disabled="Utils.isNullEmpty(userStore.userModel.PrevReservePk)">{{ $t('reservePlanningConsultation.parts.linkButton1') }}</button>
                        </li>
                    </ul>
                    <hr class="line" style="margin-bottom: 24px" />
                    <section class="form">
                        <dl>
                            <!--相談内容1-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningConsultation.parts.dl1') }}</label>
                                    </li>
                                    <li class="must">
                                        <span>{{ $t('common.parts.must') }}</span>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput spKeepFlex">
                                    <li>
                                        <BaseSelect :options="firstIssue" v-model="requestData.IssuePk1" required @childEmit="issueChange1" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--相談内容2-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningConsultation.parts.dl2') }}</label>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput spKeepFlex">
                                    <li>
                                        <BaseSelect :options="secondIssue" v-model="requestData.IssuePk2" required @childEmit="issueChange2" />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--利用方法-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningConsultation.parts.dl3') }}</label>
                                    </li>
                                    <li>
                                        <Tooltip>
                                            {{ $t('reservePlanningConsultation.parts.blowoutInformation1') }}<br />
                                            {{ $t('reservePlanningConsultation.parts.blowoutInformation2') }}<br />
                                            {{ $t('reservePlanningConsultation.parts.blowoutInformation3') }}
                                        </Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <ul class="innerBtnArea radioBtnGroup btn3">
                                            <li :class="'type' + cngMethodType" v-for="cngMethodType in reserveStore.cngMethodTypes" :key="cngMethodType">
                                                <input type="radio" :id="'radioBtn' + cngMethodType" name="radioBtnGroup" :value="cngMethodType" :checked="requestData.CngMethodType === cngMethodType" v-model="requestData.CngMethodType" @change="manegmentChange()" />
                                                <label class="radioBtn" :for="'radioBtn' + cngMethodType">{{ commonStore.getConvertDictionary('CngMethodType', cngMethodType, languageStore.getLanguageValue) }}</label>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                        <dl>
                            <!--利用言語-->
                            <dt>
                                <ul class="formLabel">
                                    <li>
                                        <label>{{ $t('reservePlanningConsultation.parts.dl4') }}</label>
                                    </li>
                                    <li>
                                        <Tooltip>{{ $t('reservePlanningConsultation.parts.blowoutInformation4') }}</Tooltip>
                                    </li>
                                </ul>
                            </dt>
                            <dd>
                                <ul class="formInput">
                                    <li>
                                        <BaseSelect :options="reserveStore.languageList" v-model="requestData.CngLanguagePk" @change="languageChange()" required />
                                    </li>
                                </ul>
                            </dd>
                        </dl>
                    </section>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--戻る-->
                            <button type="button" id="btnBack" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                        <li>
                            <!--進む-->
                            <button type="submit" id="btnNext" class="btnPrimary" @click="onAdvance">{{ $t('common.parts.nextPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style scoped>
    .statusInfo.trg1::before {
        top: 30px;
    }
</style>
